import { useState } from 'preact/hooks';
import { Card, Empty, Pill } from '../ui';
import type { Cast } from './CastView';

/** Mirrors LocationMap in @saga/spec. */
export interface Location {
	id: string;
	name: string;
	description: string;
	parent: string | null;
}
export interface LocationMap {
	title: string;
	locations: Location[];
}
export interface LocationIssue {
	severity: 'error' | 'warning';
	message: string;
	id?: string;
}

/** The chain from a location up to its top-level location (the location itself first). */
export function pathOf(map: LocationMap, id: string): Location[] {
	const out: Location[] = [];
	for (let cur = map.locations.find((l) => l.id === id); cur && !out.includes(cur); cur = cur.parent ? map.locations.find((l) => l.id === cur!.parent) : undefined) out.push(cur);
	return out;
}

/** A location and everything nested inside it. */
function within(map: LocationMap, id: string): Set<string> {
	const out = new Set([id]);
	for (let grew = true; grew; ) {
		grew = false;
		for (const l of map.locations)
			if (l.parent && out.has(l.parent) && !out.has(l.id)) {
				out.add(l.id);
				grew = true;
			}
	}
	return out;
}

function Tree(p: { map: LocationMap; parent: string | null; sel: string | null; onSelect: (id: string) => void }) {
	const kids = p.map.locations.filter((l) => l.parent === p.parent);
	if (!kids.length) return null;
	return (
		<ul class="loc-tree">
			{kids.map((l) => (
				<li key={l.id}>
					<button class={`linkish loc-node ${p.sel === l.id ? 'sel' : ''}`} onClick={() => p.onSelect(l.id)} aria-pressed={p.sel === l.id}>
						{l.name}
					</button>
					<Tree map={p.map} parent={l.id} sel={p.sel} onSelect={p.onSelect} />
				</li>
			))}
		</ul>
	);
}

/** The story's places, larger locations containing their sub-locations. */
export function LocationsView(p: { map: LocationMap; issues: LocationIssue[]; cast?: Cast | null; focus?: string | null; openCharacter?: (id: string) => void; openTimeline?: () => void }) {
	const { map } = p;
	const tops = map.locations.filter((l) => !l.parent);
	const [sel, setSel] = useState<string | null>(p.focus && map.locations.some((l) => l.id === p.focus) ? p.focus : tops[0]?.id ?? null);
	const loc = map.locations.find((l) => l.id === sel) ?? null;
	if (!map.locations.length) return <Empty>The compile found no locations in this story.</Empty>;
	const path = loc ? pathOf(map, loc.id).reverse() : [];
	const inside = loc ? within(map, loc.id) : new Set<string>();
	const here = loc ? (p.cast?.characters ?? []).filter((c) => c.appearsIn.some((a) => inside.has(a))) : [];
	const subs = loc ? map.locations.filter((l) => l.parent === loc.id) : [];
	return (
		<div class="two-col wide-left">
			<div>
				<p class="small muted">
					{map.locations.length} locations, {tops.length} top-level. Larger places contain the sub-locations listed under them.
					{p.openTimeline ? (
						<>
							{' '}
							<button class="linkish" onClick={p.openTimeline}>
								See who is where over time
							</button>
						</>
					) : null}
				</p>
				<div class="loc-tops">
					{tops.map((t) => (
						<Card key={t.id}>
							<button class={`linkish loc-node loc-top ${sel === t.id ? 'sel' : ''}`} onClick={() => setSel(t.id)} aria-pressed={sel === t.id}>
								{t.name}
							</button>
							<Tree map={map} parent={t.id} sel={sel} onSelect={setSel} />
						</Card>
					))}
				</div>
			</div>
			<div>
				{loc ? (
					<Card title={loc.name}>
						{path.length > 1 ? (
							<p class="small muted">
								{path.map((l, i) => (
									<span key={l.id}>
										{i ? ' › ' : ''}
										{l.id === loc.id ? (
											l.name
										) : (
											<button class="linkish" onClick={() => setSel(l.id)}>
												{l.name}
											</button>
										)}
									</span>
								))}
							</p>
						) : (
							<p class="row small">
								<Pill>Top-level</Pill>
							</p>
						)}
						<p class="small">
							<code>{loc.id}</code>
						</p>
						{loc.description ? <p>{loc.description}</p> : <p class="muted small">No description.</p>}
						{subs.length ? (
							<>
								<h3>Contains</h3>
								<ul class="list small">
									{subs.map((s) => (
										<li key={s.id}>
											<button class="linkish" onClick={() => setSel(s.id)}>
												{s.name}
											</button>
										</li>
									))}
								</ul>
							</>
						) : null}
						{p.cast ? (
							<>
								<h3>Characters here</h3>
								{here.length ? (
									<ul class="list small">
										{here.map((c) => (
											<li key={c.id}>
												{p.openCharacter ? (
													<button class="linkish" onClick={() => p.openCharacter!(c.id)}>
														{c.name}
													</button>
												) : (
													c.name
												)}
												<span class="muted"> · {c.tier}</span>
											</li>
										))}
									</ul>
								) : (
									<p class="small muted">No one is placed here.</p>
								)}
							</>
						) : null}
					</Card>
				) : null}
				{p.issues.length ? (
					<Card title="To check">
						<ul class="issues">
							{p.issues.map((i, n) => (
								<li key={n} class={i.severity === 'error' ? 'bad' : 'warn'}>
									{i.id ? (
										<button class="linkish" onClick={() => setSel(i.id!)}>
											{i.message}
										</button>
									) : (
										i.message
									)}
								</li>
							))}
						</ul>
					</Card>
				) : null}
				<Card>
					<details>
						<summary>Locations JSON</summary>
						<pre class="mono small">{JSON.stringify(map, null, 2)}</pre>
					</details>
				</Card>
			</div>
		</div>
	);
}
